import { defineStore } from "pinia";
import { ref } from "vue";

export interface ErrorDetail {
  /** 唯一 id（递增）*/
  id: number;
  /** 严重等级 */
  level: "error" | "warning" | "info";
  /** 标题 */
  title: string;
  /** 用户友好的主消息 */
  message: string;
  /** 详细描述（用于显示在折叠区） */
  detail?: string;
  /** 来源：哪个组件/API 调用 */
  source?: string;
  /** HTTP 状态码 */
  status?: number;
  /** 错误码（后端提供） */
  code?: string;
  /** 请求 ID */
  requestId?: string;
  /** 是否可关闭 */
  dismissible?: boolean;
  /** 是否自动消失（默认 true，1 分钟后自动消失，可显式传 false 常驻） */
  autoDismiss?: boolean;
  /** 自动消失延迟 ms（默认 60000 = 1 分钟） */
  autoDismissMs?: number;
  /** 创建时间 */
  createdAt: number;
}

let _id = 0;

export const useErrorStore = defineStore("error", () => {
  const errors = ref<ErrorDetail[]>([]);

  const push = (e: Omit<ErrorDetail, "id" | "createdAt">): number => {
    _id += 1;
    const id = _id;
    const detail: ErrorDetail = {
      id,
      dismissible: true,
      autoDismiss: true,
      ...e,
      createdAt: Date.now(),
    };
    errors.value.push(detail);
    if (detail.autoDismiss) {
      setTimeout(() => dismiss(id), detail.autoDismissMs ?? 60000);
    }
    return id;
  };

  const dismiss = (id: number) => {
    errors.value = errors.value.filter((e) => e.id !== id);
  };

  const dismissAll = () => {
    errors.value = [];
  };

  /**
   * 从未知错误（如 fetch / Error / API 响应）推导统一的 ErrorDetail
   */
  const fromUnknown = (
    err: any,
    opts?: {
      title?: string;
      source?: string;
      defaultMessage?: string;
      suggestion?: string;
      level?: "error" | "warning" | "info";
    }
  ): number => {
    const status = err?.status || err?.response?.status;
    const code = err?.code || err?.data?.code;
    const requestId = err?.requestId || err?.data?.requestId;
    const title = opts?.title || defaultTitle(status, code);
    const message = err?.message || err?.detail || err?.data?.message || opts?.defaultMessage || "未知错误";
    const detail = formatDetail({ status, code, requestId, err, suggestion: opts?.suggestion });
    return push({
      level: opts?.level || "error",
      title,
      message,
      detail,
      source: opts?.source,
      status,
      code,
      requestId,
    });
  };

  return { errors, push, dismiss, dismissAll, fromUnknown };
});

function defaultTitle(status?: number, code?: string): string {
  if (code === "ai_overloaded") return "AI 模型服务繁忙";
  if (status === 401 || code === "UNAUTHORIZED") return "登录已过期";
  if (status === 403 || code === "FORBIDDEN") return "没有权限";
  if (status === 404 || code === "NOT_FOUND") return "资源不存在";
  if (status === 422 || code === "VALIDATION") return "请求参数有误";
  if (status === 429 || code === "RATE_LIMITED") return "请求过于频繁";
  if (status === 500 || code === "INTERNAL") return "服务器内部错误";
  if (status === 502 || status === 503 || status === 504) return "服务暂不可用";
  if (typeof status === "number") return `请求失败（HTTP ${status}）`;
  return "操作失败";
}

function formatDetail(args: {
  status?: number;
  code?: string;
  requestId?: string;
  source?: string;
  err: any;
  suggestion?: string;
}): string {
  const lines: string[] = [];
  if (args.status) lines.push(`HTTP 状态: ${args.status}`);
  if (args.code) lines.push(`错误码: ${args.code}`);
  if (args.requestId) lines.push(`请求 ID: ${args.requestId}`);
  if (args.source) lines.push(`来源: ${args.source}`);

  // 提取对象/堆栈
  const e = args.err;
  if (e && typeof e === "object") {
    if (e.detail && e.detail !== e.message) lines.push(`detail: ${e.detail}`);
    if (e.data && typeof e.data === "object") {
      for (const [k, v] of Object.entries(e.data)) {
        if (k === "message") continue;
        if (typeof v === "object") continue;
        lines.push(`${k}: ${v}`);
      }
    }
    if (e.stack && typeof e.stack === "string") {
      lines.push("");
      lines.push("堆栈:");
      lines.push(e.stack.split("\n").slice(0, 8).join("\n"));
    }
  }

  if (args.suggestion) {
    lines.push("");
    lines.push("可能原因 / 建议:");
    lines.push(args.suggestion);
  }
  return lines.join("\n");
}

/**
 * 全局错误兜底：捕获未处理 promise rejection 与同步异常，
 * 自动推送到 error store，便于排查。
 */
export function installGlobalErrorHandlers(store: ReturnType<typeof useErrorStore>) {
  if (typeof window === "undefined") return;
  window.addEventListener("unhandledrejection", (e) => {
    if (!e?.reason) return;
    store.fromUnknown(e.reason, { title: "未捕获的异步错误", source: "window.unhandledrejection" });
  });
  window.addEventListener("error", (e) => {
    if (!e?.error) return;
    store.fromUnknown(e.error, { title: "脚本错误", source: `${e.filename || "?"}:${e.lineno || "?"}` });
  });
}